import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import apiService from '../services/api.service';
import { showSnackbar } from '../store/slices/uiSlice';
import GLDeterminationTable from '../components/itemRequests/GLDeterminationTable';
import {
  FIELD_LABELS,
  type GLPreview,
  type ItemFieldMeta,
  type ItemRequestDetail,
  type ItemRequestLookups,
} from '../types/itemRequests';

/**
 * Warehouse > Create New Item: request a new SAP item. The fields and their
 * lists come live from the selected company's SAP; the G/L accounts SAP's rules
 * will give the item are shown as the request is filled in. Submitting sends it
 * to the Finance review, then to the Procurement Manager; PRPO creates the item
 * in SAP after the final approval.
 */

interface FormState {
  itemCode: string;
  itemName: string;
  foreignName: string;
  itemsGroupCode: number | '';
  uomGroupEntry: number | '';
  pricingUnit: number | '';
  inventoryItem: boolean;
  salesItem: boolean;
  purchaseItem: boolean;
  active: boolean;
  barcode: string;
  udfs: Record<string, string>;
}

const EMPTY: FormState = {
  itemCode: '', itemName: '', foreignName: '', itemsGroupCode: '', uomGroupEntry: '', pricingUnit: '',
  inventoryItem: true, salesItem: false, purchaseItem: true, active: true, barcode: '', udfs: {},
};

const MAIN_FIELDS = ['U_MAIN_CATE', 'U_SUB_CAT', 'U_ITEM_TYPE', 'U_Class', 'U_USE', 'U_AUX_Code'];
const OPTIONAL_FIELDS = ['U_BRAND', 'U_Description1', 'U_Description2', 'U_PartNumber2'];

const labelOf = (f: ItemFieldMeta) => FIELD_LABELS[f.field] || f.description;
const errorMessage = (e: any, fallback: string) => e?.response?.data?.message || e?.message || fallback;

/** A UDF: a list when SAP gives values (searchable when long), otherwise free text. */
function UdfField({ meta, value, onChange, disabled }: { meta: ItemFieldMeta; value: string; onChange: (v: string) => void; disabled?: boolean }) {
  const label = labelOf(meta);
  if (meta.values.length > 12) {
    const selected = meta.values.find((v) => v.value === value) || null;
    return (
      <Autocomplete
        options={meta.values}
        value={selected}
        disabled={disabled}
        onChange={(_e, v) => onChange(v?.value || '')}
        getOptionLabel={(o) => (o.description && o.description !== o.value ? `${o.value} · ${o.description}` : o.value)}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        renderInput={(params) => <TextField {...params} label={label} required={meta.required} size="small" />}
      />
    );
  }
  if (meta.values.length > 0) {
    return (
      <TextField select fullWidth size="small" label={label} required={meta.required} value={value} disabled={disabled}
        onChange={(e) => onChange(e.target.value)}>
        {!meta.required && <MenuItem value=""><em>None</em></MenuItem>}
        {meta.values.map((v) => <MenuItem key={v.value} value={v.value}>{v.description || v.value}</MenuItem>)}
      </TextField>
    );
  }
  return (
    <TextField fullWidth size="small" label={label} required={meta.required} value={value} disabled={disabled}
      inputProps={{ maxLength: meta.size || 254 }} onChange={(e) => onChange(e.target.value)} />
  );
}

const ItemRequestForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const requestId = params.id ? Number(params.id) : null;
  const companyId = useMemo(() => Number(localStorage.getItem('selectedCompanyId') || '') || 0, []);

  const [lookups, setLookups] = useState<ItemRequestLookups | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [codeTouched, setCodeTouched] = useState(false);
  const [codeBusy, setCodeBusy] = useState(false);
  const [similar, setSimilar] = useState<Array<{ code: string; name: string }>>([]);
  const [preview, setPreview] = useState<GLPreview | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [saving, setSaving] = useState<'draft' | 'submit' | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const fields = useMemo(() => new Map((lookups?.fields || []).map((f) => [f.field, f])), [lookups]);
  const setUdf = (field: string, value: string) => setForm((f) => ({ ...f, udfs: { ...f.udfs, [field]: value } }));

  // Lookups, and the request when editing.
  useEffect(() => {
    if (!companyId) { setLoadError('Select a company first.'); return; }
    let cancelled = false;
    (async () => {
      try {
        const res: any = await apiService.getItemRequestLookups(companyId);
        if (cancelled) return;
        setLookups(res.data);
        if (requestId) {
          const r: any = await apiService.getItemRequest(requestId);
          const d: ItemRequestDetail = r.data;
          if (cancelled) return;
          if (!d.permissions.canEdit) { navigate(`/warehouse/item-requests/${requestId}`, { replace: true }); return; }
          setForm({
            itemCode: d.itemcode || '', itemName: d.itemname || '', foreignName: d.foreignname || '',
            itemsGroupCode: d.itemsgroupcode ?? '', uomGroupEntry: d.uomgroupentry ?? '', pricingUnit: d.pricingunit ?? '',
            inventoryItem: d.inventoryitem, salesItem: d.salesitem, purchaseItem: d.purchaseitem, active: d.active,
            barcode: d.barcode || '', udfs: Object.fromEntries(Object.entries(d.udfs || {}).map(([k, v]) => [k, v || ''])),
          });
          setCodeTouched(true);
        } else {
          const defaults: Record<string, string> = {};
          for (const f of res.data.fields as ItemFieldMeta[]) if (f.defaultValue) defaults[f.field] = f.defaultValue;
          setForm((s) => ({ ...s, udfs: { ...defaults, ...s.udfs } }));
        }
      } catch (e: any) {
        if (!cancelled) setLoadError(errorMessage(e, 'The item fields could not be read from SAP'));
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, requestId, navigate]);

  const proposeCode = useCallback(async (groupCode: number) => {
    setCodeBusy(true);
    try {
      const res: any = await apiService.getItemRequestNextCode(companyId, groupCode, requestId || undefined);
      setForm((f) => ({ ...f, itemCode: res?.data?.code || '' }));
    } catch {
      setForm((f) => ({ ...f, itemCode: '' }));
    } finally {
      setCodeBusy(false);
    }
  }, [companyId, requestId]);

  const onGroupChange = (groupCode: number) => {
    setForm((f) => {
      const next = { ...f, itemsGroupCode: groupCode };
      // Item Type usually carries the group's name (Safety -> ZSFE Safety).
      const typeMeta = fields.get('U_ITEM_TYPE');
      const groupName = lookups?.itemGroups.find((g) => g.code === groupCode)?.name?.toLowerCase();
      if (typeMeta && groupName && !f.udfs.U_ITEM_TYPE) {
        const match = typeMeta.values.find((v) => v.description.toLowerCase() === groupName);
        if (match) next.udfs = { ...f.udfs, U_ITEM_TYPE: match.value };
      }
      return next;
    });
    if (!codeTouched) proposeCode(groupCode);
  };

  const onMainCategoryChange = (value: string) => {
    setForm((f) => {
      const udfs: Record<string, string> = { ...f.udfs, U_MAIN_CATE: value };
      const sub = fields.get('U_SUB_CAT');
      if (sub && value.length > 4) {
        const code = value.slice(0, 4);
        if (!sub.values.length || sub.values.some((v) => v.value === code)) udfs.U_SUB_CAT = code;
      }
      return { ...f, udfs };
    });
  };

  const onUomGroupChange = (entry: number) => {
    const group = lookups?.uomGroups.find((g) => g.entry === entry);
    setForm((f) => ({ ...f, uomGroupEntry: entry, pricingUnit: group?.baseUom ?? '' }));
  };

  // Items with a similar description, shortly after typing stops.
  useEffect(() => {
    const name = form.itemName.trim();
    if (!companyId || name.length < 4) { setSimilar([]); return undefined; }
    let cancelled = false;
    const t = setTimeout(() => {
      apiService.getSimilarItems(companyId, name)
        .then((res: any) => { if (!cancelled) setSimilar(Array.isArray(res?.data) ? res.data : []); })
        .catch(() => { if (!cancelled) setSimilar([]); });
    }, 600);
    // An answer for text typed before is dropped, so it cannot replace the latest one.
    return () => { cancelled = true; clearTimeout(t); };
  }, [companyId, form.itemName]);

  // The G/L accounts, whenever the classification, group or item type changes.
  const itemClass = form.udfs.U_Class || '';
  useEffect(() => {
    if (!companyId || !lookups) return undefined;
    const classField = fields.get('U_Class');
    if (classField && !itemClass) { setPreview(null); return undefined; }
    let cancelled = false;
    setPreviewBusy(true);
    setPreviewError(null);
    const t = setTimeout(() => {
      apiService.getItemGLPreview({
        companyId,
        itemGroup: form.itemsGroupCode || null,
        itemClass: itemClass || null,
        inventoryItem: form.inventoryItem,
      })
        .then((res: any) => { if (!cancelled) setPreview(res.data); })
        .catch((e: any) => { if (!cancelled) { setPreview(null); setPreviewError(errorMessage(e, 'The G/L accounts could not be read from SAP')); } })
        .finally(() => { if (!cancelled) setPreviewBusy(false); });
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [companyId, lookups, fields, itemClass, form.itemsGroupCode, form.inventoryItem]);

  const save = async (submit: boolean) => {
    setSaving(submit ? 'submit' : 'draft');
    setSaveError(null);
    const payload = {
      companyId,
      itemCode: form.itemCode.trim() || null,
      itemName: form.itemName,
      foreignName: form.foreignName,
      itemsGroupCode: form.itemsGroupCode || null,
      uomGroupEntry: form.uomGroupEntry || null,
      pricingUnit: form.pricingUnit || null,
      inventoryItem: form.inventoryItem,
      salesItem: form.salesItem,
      purchaseItem: form.purchaseItem,
      active: form.active,
      barcode: form.barcode,
      udfs: form.udfs,
      submit,
    };
    try {
      const res: any = requestId
        ? await apiService.updateItemRequest(requestId, payload)
        : await apiService.createItemRequest(payload);
      dispatch(showSnackbar({ message: submit ? `${res.data.requestno} was sent to the Finance review` : `${res.data.requestno} was saved as a draft`, severity: 'success' }));
      navigate(`/warehouse/item-requests/${res.data.requestid}`);
    } catch (e: any) {
      setSaveError(errorMessage(e, 'The request could not be saved'));
    } finally {
      setSaving(null);
    }
  };

  if (loadError) return <Box sx={{ p: { xs: 2, md: 3 } }}><Alert severity="error">{loadError}</Alert></Box>;
  if (!lookups) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 3 }} role="status">
        <CircularProgress size={22} /><Typography>Reading the item fields from SAP…</Typography>
      </Stack>
    );
  }

  const selectedUomGroup = lookups.uomGroups.find((g) => g.entry === form.uomGroupEntry);
  const mainFields = MAIN_FIELDS.map((f) => fields.get(f)).filter(Boolean) as ItemFieldMeta[];
  const optionalFields = OPTIONAL_FIELDS.map((f) => fields.get(f)).filter(Boolean) as ItemFieldMeta[];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'grid', gap: 2.5, maxWidth: 1200 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <IconButton aria-label="Back to the item requests" onClick={() => navigate('/warehouse/item-requests')}><ArrowBackIcon /></IconButton>
        <Box>
          <Typography variant="h5" component="h1">{requestId ? 'Edit item request' : 'Create New Item'}</Typography>
          <Typography variant="body2" color="text.secondary">
            Finance checks the G/L accounts, the Procurement Manager approves, then PRPO creates the item in SAP.
          </Typography>
        </Box>
      </Stack>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>Item</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField select fullWidth size="small" required label="Item Group" value={form.itemsGroupCode}
              onChange={(e) => onGroupChange(Number(e.target.value))}>
              {lookups.itemGroups.map((g) => <MenuItem key={g.code} value={g.code}>{g.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" label="Item No." value={form.itemCode}
              onChange={(e) => { setCodeTouched(true); setForm((f) => ({ ...f, itemCode: e.target.value })); }}
              helperText={codeTouched ? 'Checked against SAP when you submit' : 'The item group’s next number'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {codeBusy ? <CircularProgress size={16} /> : (
                      <Tooltip title="Propose the group’s next number">
                        <span>
                          <IconButton size="small" aria-label="Propose the group's next number" disabled={!form.itemsGroupCode}
                            onClick={() => { setCodeTouched(false); if (form.itemsGroupCode) proposeCode(Number(form.itemsGroupCode)); }}>
                            <AutorenewIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </InputAdornment>
                ),
              }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%' }}>
              <FormControlLabel control={<Switch checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />}
                label={form.active ? 'Active' : 'Inactive'} />
            </Stack>
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField fullWidth size="small" required label="Description" value={form.itemName} inputProps={{ maxLength: 200 }}
              onChange={(e) => setForm((f) => ({ ...f, itemName: e.target.value }))} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" label="Foreign Name" value={form.foreignName} inputProps={{ maxLength: 200 }}
              onChange={(e) => setForm((f) => ({ ...f, foreignName: e.target.value }))} />
          </Grid>
          {similar.length > 0 && (
            <Grid item xs={12}>
              <Alert severity="info">
                Items with a similar description already exist: {similar.map((s) => `${s.code} ${s.name}`).join(' · ')}. Check that the item is really new.
              </Alert>
            </Grid>
          )}
          {mainFields.map((meta) => (
            <Grid item xs={12} md={4} key={meta.field}>
              <UdfField meta={meta} value={form.udfs[meta.field] || ''}
                onChange={(v) => (meta.field === 'U_MAIN_CATE' ? onMainCategoryChange(v) : setUdf(meta.field, v))} />
            </Grid>
          ))}
          <Grid item xs={12} md={4}>
            <TextField select fullWidth size="small" required label="UoM Group" value={form.uomGroupEntry}
              onChange={(e) => onUomGroupChange(Number(e.target.value))}>
              {lookups.uomGroups.map((g) => <MenuItem key={g.entry} value={g.entry}>{g.code}{g.name && g.name !== g.code ? ` · ${g.name}` : ''}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField select fullWidth size="small" label="Pricing Unit" value={form.pricingUnit} disabled={!selectedUomGroup}
              onChange={(e) => setForm((f) => ({ ...f, pricingUnit: Number(e.target.value) }))}>
              {(selectedUomGroup?.uoms || []).map((u) => <MenuItem key={u.entry} value={u.entry}>{u.code}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack direction="row" flexWrap="wrap" columnGap={1}>
              <FormControlLabel control={<Checkbox checked={form.inventoryItem} onChange={(e) => setForm((f) => ({ ...f, inventoryItem: e.target.checked }))} />} label="Inventory Item" />
              <FormControlLabel control={<Checkbox checked={form.salesItem} onChange={(e) => setForm((f) => ({ ...f, salesItem: e.target.checked }))} />} label="Sales Item" />
              <FormControlLabel control={<Checkbox checked={form.purchaseItem} onChange={(e) => setForm((f) => ({ ...f, purchaseItem: e.target.checked }))} />} label="Purchasing Item" />
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>Optional</Typography>
        <Grid container spacing={2}>
          {optionalFields.map((meta) => (
            <Grid item xs={12} md={4} key={meta.field}>
              <UdfField meta={meta} value={form.udfs[meta.field] || ''} onChange={(v) => setUdf(meta.field, v)} />
            </Grid>
          ))}
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" label="Bar Code" value={form.barcode} inputProps={{ maxLength: 254 }}
              onChange={(e) => setForm((f) => ({ ...f, barcode: e.target.value }))} />
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>G/L accounts SAP will use</Typography>
        <GLDeterminationTable preview={preview} loading={previewBusy} error={previewError}
          emptyHint="Choose the Classification to see the G/L accounts SAP's rules give this item." />
      </Paper>

      {saveError && <Alert severity="error">{saveError}</Alert>}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
        <Button onClick={() => navigate(requestId ? `/warehouse/item-requests/${requestId}` : '/warehouse/item-requests')} disabled={!!saving}>Cancel</Button>
        <Button variant="outlined" onClick={() => save(false)} disabled={!!saving}>
          {saving === 'draft' ? 'Saving…' : 'Save draft'}
        </Button>
        <Button variant="contained" onClick={() => save(true)} disabled={!!saving}>
          {saving === 'submit' ? 'Sending…' : 'Send to Finance review'}
        </Button>
      </Stack>
    </Box>
  );
};

export default ItemRequestForm;
